import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/shared/SEO';
import * as THREE from 'three';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import {
    Coins,
    Sparkles,
    ShoppingCart,
    Plus,
    ChevronLeft,
    RotateCcw,
    Zap,
    Star,
} from 'lucide-react';

interface CropType {
    id: string;
    name: string;
    emoji: string;
    growTime: number;
    sellPrice: number;
    seedPrice: number;
    color: string;
    exp: number;
    rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
    holiday?: {
        name: string;
        startMonth: number;
        startDay: number;
        endMonth: number;
        endDay: number;
    };
}

const CROPS: CropType[] = [
    // 初级作物 - 普通 (5-30秒)
    { id: 'cabbage', name: '白菜', emoji: '🥬', growTime: 5, sellPrice: 5, seedPrice: 3, color: '#22c55e', exp: 2, rarity: 'common' },
    { id: 'radish', name: '萝卜', emoji: '🥕', growTime: 8, sellPrice: 8, seedPrice: 5, color: '#f97316', exp: 4, rarity: 'common' },
    { id: 'wheat', name: '小麦', emoji: '🌾', growTime: 10, sellPrice: 10, seedPrice: 6, color: '#eab308', exp: 5, rarity: 'common' },
    { id: 'potato', name: '土豆', emoji: '🥔', growTime: 12, sellPrice: 15, seedPrice: 8, color: '#d4a574', exp: 8, rarity: 'common' },
    { id: 'cucumber', name: '黄瓜', emoji: '🥒', growTime: 15, sellPrice: 20, seedPrice: 10, color: '#22c55e', exp: 10, rarity: 'common' },
    { id: 'onion', name: '洋葱', emoji: '🧅', growTime: 18, sellPrice: 25, seedPrice: 12, color: '#fbbf24', exp: 12, rarity: 'common' },
    { id: 'garlic', name: '大蒜', emoji: '🧄', growTime: 20, sellPrice: 30, seedPrice: 15, color: '#f5f5dc', exp: 15, rarity: 'common' },
    // 初级作物 - 优秀 (45-90秒)
    { id: 'tomato', name: '西红柿', emoji: '🍅', growTime: 45, sellPrice: 60, seedPrice: 35, color: '#ef4444', exp: 30, rarity: 'uncommon' },
    { id: 'corn', name: '玉米', emoji: '🌽', growTime: 60, sellPrice: 80, seedPrice: 45, color: '#eab308', exp: 40, rarity: 'uncommon' },
    { id: 'eggplant', name: '茄子', emoji: '🍆', growTime: 75, sellPrice: 100, seedPrice: 55, color: '#8b5cf6', exp: 50, rarity: 'uncommon' },
    // 中级作物 - 优秀 (90-150秒)
    { id: 'broccoli', name: '西兰花', emoji: '🥦', growTime: 90, sellPrice: 120, seedPrice: 70, color: '#228b22', exp: 60, rarity: 'uncommon' },
    { id: 'pepper', name: '辣椒', emoji: '🌶️', growTime: 120, sellPrice: 150, seedPrice: 85, color: '#dc2626', exp: 75, rarity: 'uncommon' },
    // 中级作物 - 稀有 (4-8分钟)
    { id: 'pumpkin', name: '南瓜', emoji: '🎃', growTime: 240, sellPrice: 300, seedPrice: 180, color: '#f97316', exp: 150, rarity: 'rare' },
    { id: 'strawberry', name: '草莓', emoji: '🍓', growTime: 300, sellPrice: 380, seedPrice: 220, color: '#ec4899', exp: 190, rarity: 'rare' },
    { id: 'blueberry', name: '蓝莓', emoji: '🫐', growTime: 360, sellPrice: 450, seedPrice: 260, color: '#6366f1', exp: 225, rarity: 'rare' },
    { id: 'lemon', name: '柠檬', emoji: '🍋', growTime: 400, sellPrice: 500, seedPrice: 290, color: '#facc15', exp: 250, rarity: 'rare' },
    { id: 'apple', name: '苹果', emoji: '🍎', growTime: 420, sellPrice: 550, seedPrice: 320, color: '#ef4444', exp: 275, rarity: 'rare' },
    { id: 'orange', name: '橙子', emoji: '🍊', growTime: 480, sellPrice: 600, seedPrice: 350, color: '#fb923c', exp: 300, rarity: 'rare' },
    // 高级作物 - 史诗 (15-40分钟)
    { id: 'watermelon', name: '西瓜', emoji: '🍉', growTime: 900, sellPrice: 1000, seedPrice: 600, color: '#16a34a', exp: 500, rarity: 'epic' },
    { id: 'grape', name: '葡萄', emoji: '🍇', growTime: 1200, sellPrice: 1300, seedPrice: 780, color: '#8b5cf6', exp: 650, rarity: 'epic' },
    { id: 'mango', name: '芒果', emoji: '🥭', growTime: 1500, sellPrice: 1600, seedPrice: 960, color: '#facc15', exp: 800, rarity: 'epic' },
    { id: 'pineapple', name: '菠萝', emoji: '🍍', growTime: 1800, sellPrice: 1900, seedPrice: 1140, color: '#eab308', exp: 950, rarity: 'epic' },
    { id: 'cherry', name: '樱桃', emoji: '🍒', growTime: 2000, sellPrice: 2200, seedPrice: 1320, color: '#dc2626', exp: 1100, rarity: 'epic' },
    { id: 'peach', name: '桃子', emoji: '🍑', growTime: 2400, sellPrice: 2500, seedPrice: 1500, color: '#fb7185', exp: 1250, rarity: 'epic' },
    // 稀有作物 - 传说 (1-4小时)
    { id: 'lychee', name: '荔枝', emoji: '🥝', growTime: 3600, sellPrice: 5000, seedPrice: 3000, color: '#f472b6', exp: 2500, rarity: 'legendary' },
    { id: 'dragonfruit', name: '火龙果', emoji: '🌺', growTime: 4500, sellPrice: 7000, seedPrice: 4200, color: '#f43f5e', exp: 3500, rarity: 'legendary' },
    { id: 'pomegranate', name: '石榴', emoji: '🔴', growTime: 5400, sellPrice: 9000, seedPrice: 5400, color: '#be123c', exp: 4500, rarity: 'legendary' },
    { id: 'coconut', name: '椰子', emoji: '🥥', growTime: 6000, sellPrice: 12000, seedPrice: 7200, color: '#f5f5dc', exp: 6000, rarity: 'legendary' },
    // 终极传说 (6-12小时)
    { id: 'durian', name: '榴莲', emoji: '💜', growTime: 7200, sellPrice: 20000, seedPrice: 12000, color: '#a855f7', exp: 10000, rarity: 'legendary' },
    { id: 'snowflake', name: '雪莲果', emoji: '❄️', growTime: 9000, sellPrice: 30000, seedPrice: 18000, color: '#67e8f9', exp: 15000, rarity: 'legendary' },
    { id: 'golden', name: '金苹果', emoji: '🍏', growTime: 10800, sellPrice: 40000, seedPrice: 24000, color: '#fbbf24', exp: 20000, rarity: 'legendary' },
    { id: 'crystal', name: '水晶葡萄', emoji: '💎', growTime: 12600, sellPrice: 50000, seedPrice: 30000, color: '#a78bfa', exp: 25000, rarity: 'legendary' },
    { id: 'magic', name: '魔法果实', emoji: '🌟', growTime: 14400, sellPrice: 80000, seedPrice: 48000, color: '#f59e0b', exp: 40000, rarity: 'legendary' },
    // ===== 节假日限定作物 =====
    // 🧧 春节系列 (1月20日-2月20日)
    { id: 'springglutinous', name: '年糕', emoji: '🍡', growTime: 1800, sellPrice: 3500, seedPrice: 2000, color: '#f0f0f0', exp: 1800, rarity: 'epic', holiday: { name: '春节', startMonth: 1, startDay: 20, endMonth: 2, endDay: 20 } },
    { id: 'springdumpling', name: '饺子', emoji: '🥟', growTime: 2400, sellPrice: 5000, seedPrice: 3000, color: '#ff6b6b', exp: 2500, rarity: 'epic', holiday: { name: '春节', startMonth: 1, startDay: 20, endMonth: 2, endDay: 20 } },
    { id: 'soupball', name: '汤圆', emoji: '⚪', growTime: 3000, sellPrice: 6000, seedPrice: 3500, color: '#ffffff', exp: 3000, rarity: 'epic', holiday: { name: '元宵节', startMonth: 1, startDay: 20, endMonth: 2, endDay: 20 } },
    // 🥮 中秋节 (9月15日-10月5日)
    { id: 'mooncake', name: '月饼', emoji: '🥮', growTime: 3600, sellPrice: 8000, seedPrice: 5000, color: '#f4a460', exp: 4000, rarity: 'legendary', holiday: { name: '中秋节', startMonth: 9, startDay: 15, endMonth: 10, endDay: 5 } },
    { id: 'lantern', name: '灯笼', emoji: '🏮', growTime: 4200, sellPrice: 10000, seedPrice: 6000, color: '#ff4444', exp: 5000, rarity: 'legendary', holiday: { name: '中秋节', startMonth: 9, startDay: 15, endMonth: 10, endDay: 5 } },
    // 🎄 圣诞节 (12月20日-1月5日)
    { id: 'christmasstar', name: '圣诞树', emoji: '🎄', growTime: 5400, sellPrice: 15000, seedPrice: 9000, color: '#228b22', exp: 7500, rarity: 'legendary', holiday: { name: '圣诞节', startMonth: 12, startDay: 20, endMonth: 1, endDay: 5 } },
    { id: 'candycane', name: '拐杖糖', emoji: '🍭', growTime: 3000, sellPrice: 6000, seedPrice: 3600, color: '#ff0000', exp: 3000, rarity: 'epic', holiday: { name: '圣诞节', startMonth: 12, startDay: 20, endMonth: 1, endDay: 5 } },
    { id: 'christmascookie', name: '圣诞姜饼', emoji: '🍪', growTime: 3600, sellPrice: 7000, seedPrice: 4200, color: '#d2691e', exp: 3500, rarity: 'epic', holiday: { name: '圣诞节', startMonth: 12, startDay: 20, endMonth: 1, endDay: 5 } },
    // 💝 情人节 (2月10日-2月20日)
    { id: 'rose', name: '玫瑰', emoji: '🌹', growTime: 4800, sellPrice: 12000, seedPrice: 7200, color: '#ff1493', exp: 6000, rarity: 'legendary', holiday: { name: '情人节', startMonth: 2, startDay: 10, endMonth: 2, endDay: 20 } },
    { id: 'chocolate', name: '巧克力', emoji: '🍫', growTime: 3600, sellPrice: 8000, seedPrice: 4800, color: '#8b4513', exp: 4000, rarity: 'epic', holiday: { name: '情人节', startMonth: 2, startDay: 10, endMonth: 2, endDay: 20 } },
    // 🎃 万圣节 (10月20日-11月5日)
    { id: 'halloweenpumpkin', name: '杰克南瓜灯', emoji: '🕷️', growTime: 5400, sellPrice: 13000, seedPrice: 7800, color: '#ff8c00', exp: 6500, rarity: 'legendary', holiday: { name: '万圣节', startMonth: 10, startDay: 20, endMonth: 11, endDay: 5 } },
    { id: 'candy', name: '糖果', emoji: '🍬', growTime: 2400, sellPrice: 4500, seedPrice: 2700, color: '#9932cc', exp: 2250, rarity: 'epic', holiday: { name: '万圣节', startMonth: 10, startDay: 20, endMonth: 11, endDay: 5 } },
    // 🎆 元旦/新年 (12月25日-1月10日)
    { id: 'firework', name: '烟花树', emoji: '🎆', growTime: 7200, sellPrice: 20000, seedPrice: 12000, color: '#ff4500', exp: 10000, rarity: 'legendary', holiday: { name: '元旦', startMonth: 12, startDay: 25, endMonth: 1, endDay: 10 } },
    { id: 'luckypacket', name: '红包', emoji: '🧧', growTime: 6000, sellPrice: 18000, seedPrice: 10800, color: '#ff0000', exp: 9000, rarity: 'legendary', holiday: { name: '元旦', startMonth: 12, startDay: 25, endMonth: 1, endDay: 10 } },
    // 🌸 端午节 (6月1日-6月15日)
    { id: 'zongzi', name: '粽子', emoji: '🫔', growTime: 3600, sellPrice: 7500, seedPrice: 4500, color: '#228b22', exp: 3750, rarity: 'epic', holiday: { name: '端午节', startMonth: 6, startDay: 1, endMonth: 6, endDay: 15 } },
    { id: 'savoryrice', name: '咸蛋黄', emoji: '🥚', growTime: 3000, sellPrice: 5500, seedPrice: 3300, color: '#ffd700', exp: 2750, rarity: 'epic', holiday: { name: '端午节', startMonth: 6, startDay: 1, endMonth: 6, endDay: 15 } },
    // 🌼 清明节 (4月3日-4月6日)
    { id: 'tangyuan', name: '青团', emoji: '🍡', growTime: 2400, sellPrice: 4500, seedPrice: 2700, color: '#7cb342', exp: 2250, rarity: 'epic', holiday: { name: '清明节', startMonth: 4, startDay: 3, endMonth: 4, endDay: 6 } },
    { id: 'flower', name: '菊花', emoji: '🌼', growTime: 3000, sellPrice: 6000, seedPrice: 3600, color: '#fdd835', exp: 3000, rarity: 'epic', holiday: { name: '清明节', startMonth: 4, startDay: 3, endMonth: 4, endDay: 6 } },
    // 🛠️ 劳动节 (4月25日-5月5日)
    { id: 'bread', name: '劳动面包', emoji: '🍞', growTime: 3600, sellPrice: 7000, seedPrice: 4200, color: '#d4a574', exp: 3500, rarity: 'epic', holiday: { name: '劳动节', startMonth: 4, startDay: 25, endMonth: 5, endDay: 5 } },
    { id: 'beer', name: '啤酒', emoji: '🍺', growTime: 4200, sellPrice: 8500, seedPrice: 5100, color: '#f4a460', exp: 4250, rarity: 'epic', holiday: { name: '劳动节', startMonth: 4, startDay: 25, endMonth: 5, endDay: 5 } },
];

interface Plot {
    id: number;
    cropId: string | null;
    plantedAt: number | null;
}

interface GameState {
    coins: number;
    exp: number;
    level: number;
    plots: Plot[];
    inventory: Record<string, number>;
    seedInventory: Record<string, number>;
    holidaySeeds: Record<string, number>;
}

interface ParticleData {
    x: number;
    y: number;
    z: number;
    vx: number;
    vy: number;
    vz: number;
    life: number;
    maxLife: number;
    color: number;
    size: number;
    type: 'coin' | 'exp' | 'sparkle';
}

interface PopupData3D {
    id: string;
    x: number;
    y: number;
    z: number;
    text: string;
    color: string;
    life: number;
    maxLife: number;
    object: CSS2DObject;
}

const INITIAL_PLOTS = 20;
const getExpandCost = (plotCount: number) => Math.floor(150 * Math.pow(1.15, plotCount - 6));
const STORAGE_KEY = 'lingdux-farm-save-v4';
const COLS = 4;

// Three.js isometric settings
const TILE_SIZE = 12;

const canPlantHolidayCrop = (_crop: CropType): boolean => {
    return true;
};

const tryGetHolidaySeed = (): CropType | null => {
    const holidayCrops = CROPS.filter(c => c.holiday);
    if (holidayCrops.length === 0) return null;
    if (Math.random() < 0.15) {
        const randomCrop = holidayCrops[Math.floor(Math.random() * holidayCrops.length)];
        return randomCrop;
    }
    return null;
};

// Isometric coordinate conversion for Three.js
const isoToWorld = (col: number, row: number, gridOffsetX: number, gridOffsetY: number): THREE.Vector3 => {
    const x = (col - row) * (TILE_SIZE / 2) + gridOffsetX;
    const z = (col + row) * (TILE_SIZE / 2) + gridOffsetY;
    return new THREE.Vector3(x, 0, z);
};

function createDiamondGeometry(width: number, _height: number): THREE.BufferGeometry {
    const geometry = new THREE.BufferGeometry();
    const w = width / 2;
    const vertices = new Float32Array([
        // 底面（深色）
        0, 0, -w,
        w, 0, 0,
        0, 0, w,
        -w, 0, 0,
        // 顶面（浅色，略微抬高）
        0, 0.2, -w * 0.85,
        w * 0.85, 0.2, 0,
        0, 0.2, w * 0.85,
        -w * 0.85, 0.2, 0,
    ]);
    const indices = [
        // 底面
        0, 1, 2,
        0, 2, 3,
        // 顶面
        4, 6, 5,
        4, 7, 6,
        // 侧面
        0, 4, 1,
        1, 4, 5,
        1, 5, 2,
        2, 5, 6,
        2, 6, 3,
        3, 6, 7,
        3, 7, 0,
        0, 7, 4,
    ];
    geometry.setIndex(indices);
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geometry.computeVertexNormals();
    return geometry;
}

const Farm: React.FC = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const threeContainerRef = useRef<HTMLDivElement>(null);
    const sceneRef = useRef<THREE.Scene | null>(null);
    const cameraRef = useRef<THREE.OrthographicCamera | null>(null);
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const labelRendererRef = useRef<CSS2DRenderer | null>(null);
    const controlsRef = useRef<OrbitControls | null>(null);
    const plotMeshesRef = useRef<Map<number, THREE.Mesh>>(new Map());
    const cropLabelsRef = useRef<Map<number, CSS2DObject>>(new Map());
    const glowMeshesRef = useRef<Map<number, THREE.Mesh>>(new Map());
    const particlesRef = useRef<ParticleData[]>([]);
    const particlePointsRef = useRef<THREE.Points | null>(null);
    const popupsRef = useRef<PopupData3D[]>([]);
    const popupLabelsRef = useRef<Map<string, CSS2DObject>>(new Map());
    const animationFrameRef = useRef<number>(0);
    const gridOffsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

    const [gameState, setGameState] = useState<GameState>(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                return {
                    ...parsed,
                    holidaySeeds: parsed.holidaySeeds || {},
                    seedInventory: parsed.seedInventory || {},
                };
            } catch {
                // ignore
            }
        }
        return {
            coins: 300,
            exp: 0,
            level: 1,
            plots: Array.from({ length: INITIAL_PLOTS }, (_, i) => ({ id: i, cropId: null, plantedAt: null })),
            inventory: {},
            seedInventory: {},
            holidaySeeds: {},
        };
    });

    const [selectedPlot, setSelectedPlot] = useState<number | null>(null);
    const [showShop, setShowShop] = useState(false);
    const [showWarehouse, setShowWarehouse] = useState(false);
    const [showSeedWarehouse, setShowSeedWarehouse] = useState(false);
    const [showCropInfo, setShowCropInfo] = useState(false);
    const [selectedCropForInfo, setSelectedCropForInfo] = useState<CropType | null>(null);
    const [selectedPlotForInfo, setSelectedPlotForInfo] = useState<Plot | null>(null);
    const [showHolidaySeedPopup, setShowHolidaySeedPopup] = useState(false);
    const [holidaySeedCrop, setHolidaySeedCrop] = useState<CropType | null>(null);
    const [currentTime, setCurrentTime] = useState(Date.now());
    const [hoveredPlot, setHoveredPlot] = useState<number | null>(null);
    const [animationFrame, setAnimationFrame] = useState(0);

    const levelExp = (lvl: number) => Math.floor(100 * Math.pow(1.5, lvl - 1));

    // Save game state
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(gameState));
    }, [gameState]);

    // Update timer
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(Date.now());
            setAnimationFrame(f => f + 1);
        }, 50);
        return () => clearInterval(interval);
    }, []);

    // Initialize Three.js
    useEffect(() => {
        if (!threeContainerRef.current) return;

        const initThree = async () => {
            try {
                await new Promise(resolve => requestAnimationFrame(resolve));
                await new Promise(resolve => requestAnimationFrame(resolve));

                const containerWidth = threeContainerRef.current?.clientWidth || 500;
                const containerHeight = 450;

                console.log('[Three.js] Initializing with size:', containerWidth, 'x', containerHeight);

                // Scene
                const scene = new THREE.Scene();
                scene.background = new THREE.Color(0x87ceeb);
                sceneRef.current = scene;

                // Orthographic camera (isometric view)
                const aspect = containerWidth / containerHeight;
                const frustumSize = 120;
                const camera = new THREE.OrthographicCamera(
                    frustumSize * aspect / -2,
                    frustumSize * aspect / 2,
                    frustumSize / 2,
                    frustumSize / -2,
                    0.1,
                    1000
                );
                // Isometric view: camera at diagonal of cube (equal distance from all axes)
                camera.position.set(60, 60, 60);
                camera.lookAt(0, 0, 0);
                cameraRef.current = camera;

                // WebGL renderer with shadows
                const renderer = new THREE.WebGLRenderer({ antialias: true });
                renderer.setSize(containerWidth, containerHeight);
                renderer.setPixelRatio(window.devicePixelRatio);
                renderer.shadowMap.enabled = true;
                renderer.shadowMap.type = THREE.PCFSoftShadowMap;
                if (!threeContainerRef.current) return;
                threeContainerRef.current.appendChild(renderer.domElement);
                rendererRef.current = renderer;

                // Lighting for better visuals
                const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
                scene.add(ambientLight);
                
                const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
                directionalLight.position.set(30, 50, 30);
                directionalLight.castShadow = true;
                directionalLight.shadow.mapSize.width = 1024;
                directionalLight.shadow.mapSize.height = 1024;
                directionalLight.shadow.camera.near = 0.1;
                directionalLight.shadow.camera.far = 200;
                scene.add(directionalLight);

                // CSS2D renderer for emoji labels
                const labelRenderer = new CSS2DRenderer();
                labelRenderer.setSize(containerWidth, containerHeight);
                labelRenderer.domElement.style.position = 'absolute';
                labelRenderer.domElement.style.top = '0';
                labelRenderer.domElement.style.pointerEvents = 'none';
                threeContainerRef.current.appendChild(labelRenderer.domElement);
                labelRendererRef.current = labelRenderer;

                // OrbitControls (disable rotation)
                const controls = new OrbitControls(camera, renderer.domElement);
                controls.enableRotate = false;
                controls.enableZoom = true;
                controls.enablePan = false;
                controls.target.set(0, 0, 0);
                controlsRef.current = controls;

                // Create plots
                createPlots();

                // Create particle system
                const particleGeometry = new THREE.BufferGeometry();
                const maxParticles = 500;
                const positions = new Float32Array(maxParticles * 3);
                const colors = new Float32Array(maxParticles * 3);
                const sizes = new Float32Array(maxParticles);

                particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
                particleGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
                particleGeometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

                const particleMaterial = new THREE.PointsMaterial({
                    size: 0.3,
                    vertexColors: true,
                    transparent: true,
                    opacity: 0.8,
                    sizeAttenuation: true,
                });

                const particlePoints = new THREE.Points(particleGeometry, particleMaterial);
                scene.add(particlePoints);
                particlePointsRef.current = particlePoints;

                // Animation loop
                let frameId = 0;
                const animate = () => {
                    frameId = requestAnimationFrame(animate);
                    animationFrameRef.current++;
                    controls.update();
                    updateParticles();
                    updatePopups();
                    updateCropLabels();
                    renderer.render(scene, camera);
                    labelRenderer.render(scene, camera);
                };
                animate();

                // Handle resize
                const handleResize = () => {
                    if (!threeContainerRef.current || !camera || !renderer) return;
                    const width = threeContainerRef.current.clientWidth || 500;
                    const height = 450;

                    camera.left = frustumSize * (width / height) / -2;
                    camera.right = frustumSize * (width / height) / 2;
                    camera.top = frustumSize / 2;
                    camera.bottom = frustumSize / -2;
                    camera.updateProjectionMatrix();

                    renderer.setSize(width, height);
                    labelRenderer.setSize(width, height);
                    redrawPlots();
                };
                window.addEventListener('resize', handleResize);

                return () => {
                    window.removeEventListener('resize', handleResize);
                    cancelAnimationFrame(frameId);
                    renderer.dispose();
                    controls.dispose();
                };
            } catch (e) {
                console.error('[Three.js] Error:', e);
            }
        };

        initThree();

        return () => {
            if (rendererRef.current && threeContainerRef.current) {
                threeContainerRef.current.removeChild(rendererRef.current.domElement);
            }
        };
    }, []);

    const createPlots = () => {
        const scene = sceneRef.current;
        if (!scene) return;

        console.log('[createPlots] Creating', gameState.plots.length, 'plots');

        const rows = Math.ceil(gameState.plots.length / COLS);

        // Calculate grid offset to center the isometric grid at world origin
        // isoToWorld: x = (col - row) * TILE_SIZE/2 + gridOffsetX, z = (col + row) * TILE_SIZE/2 + gridOffsetY
        // To center: when col=colCenter, row=rowCenter -> x=0, z=0
        const colCenter = (COLS - 1) / 2;
        const rowCenter = (rows - 1) / 2;
        gridOffsetRef.current = {
            x: -(colCenter - rowCenter) * (TILE_SIZE / 2),
            y: -(colCenter + rowCenter) * (TILE_SIZE / 2)
        };

        // Add ground plane (grass) - fills the entire view
        const groundGeometry = new THREE.PlaneGeometry(200, 200);
        const groundMaterial = new THREE.MeshLambertMaterial({ color: 0x7cb860 });
        const ground = new THREE.Mesh(groundGeometry, groundMaterial);
        ground.rotation.x = -Math.PI / 2;
        ground.position.y = -0.05;
        ground.receiveShadow = true;
        scene.add(ground);

        gameState.plots.forEach((plot, index) => {
            const col = index % COLS;
            const row = Math.floor(index / COLS);
            const worldPos = isoToWorld(col, row, gridOffsetRef.current.x, gridOffsetRef.current.y);

            // Create diamond plot mesh with better material
            const plotGeometry = createDiamondGeometry(TILE_SIZE * 0.9, TILE_SIZE * 0.9);
            const plotMaterial = new THREE.MeshLambertMaterial({ 
                color: 0xcd853f,
                flatShading: false,
            });
            const plotMesh = new THREE.Mesh(plotGeometry, plotMaterial);
            plotMesh.position.set(worldPos.x, 0, worldPos.z);
            plotMesh.userData = { plotId: plot.id };
            plotMesh.castShadow = true;
            plotMesh.receiveShadow = true;
            scene.add(plotMesh);
            plotMeshesRef.current.set(plot.id, plotMesh);

            // Create crop label
            const cropLabel = createCropLabelObject();
            cropLabel.position.set(worldPos.x, 1, worldPos.z);
            scene.add(cropLabel);
            cropLabelsRef.current.set(plot.id, cropLabel);

            // Create glow mesh (hidden by default)
            const glowGeometry = new THREE.SphereGeometry(1, 16, 16);
            const glowMaterial = new THREE.MeshBasicMaterial({
                color: 0xffd700,
                transparent: true,
                opacity: 0,
            });
            const glowMesh = new THREE.Mesh(glowGeometry, glowMaterial);
            glowMesh.position.set(worldPos.x, 1, worldPos.z);
            scene.add(glowMesh);
            glowMeshesRef.current.set(plot.id, glowMesh);
        });

        // Add click handler
        const canvas = rendererRef.current?.domElement;
        if (canvas) {
            canvas.addEventListener('pointerdown', onPointerClick as EventListener);
            canvas.addEventListener('pointermove', onPointerMove as EventListener);
        }
    };

    const createCropLabelObject = (): CSS2DObject => {
        const div = document.createElement('div');
        div.style.fontSize = '24px';
        div.style.pointerEvents = 'none';
        div.style.textAlign = 'center';
        div.style.transform = 'translate(-50%, -100%)';
        const label = new CSS2DObject(div);
        label.userData = { div };
        return label;
    };

    const onPointerClick = (event: PointerEvent) => {
        const scene = sceneRef.current;
        const camera = cameraRef.current;
        const renderer = rendererRef.current;
        if (!scene || !camera || !renderer) return;

        const rect = renderer.domElement.getBoundingClientRect();
        const mouse = new THREE.Vector2();
        mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mouse, camera);
        const plotMeshes = Array.from(plotMeshesRef.current.values());
        const intersects = raycaster.intersectObjects(plotMeshes);

        if (intersects.length > 0) {
            const plotId = intersects[0].object.userData.plotId;
            handlePlotClick(plotId);
        }
    };

    const onPointerMove = (event: PointerEvent) => {
        const scene = sceneRef.current;
        const camera = cameraRef.current;
        const renderer = rendererRef.current;
        if (!scene || !camera || !renderer) return;

        const rect = renderer.domElement.getBoundingClientRect();
        const mouse = new THREE.Vector2();
        mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mouse, camera);
        const plotMeshes = Array.from(plotMeshesRef.current.values());
        const intersects = raycaster.intersectObjects(plotMeshes);

        if (intersects.length > 0) {
            const plotId = intersects[0].object.userData.plotId;
            setHoveredPlot(plotId);
            renderer.domElement.style.cursor = 'pointer';
        } else {
            setHoveredPlot(null);
            renderer.domElement.style.cursor = 'default';
        }
    };

    const updatePlotVisuals = useCallback(() => {
        const scene = sceneRef.current;
        if (!scene) return;

        gameState.plots.forEach((plot) => {
            const plotMesh = plotMeshesRef.current.get(plot.id);
            if (!plotMesh) return;

            const crop = plot.cropId ? CROPS.find(c => c.id === plot.cropId) : null;
            const stage = crop && plot.plantedAt ? getGrowthStage(plot) : 0;
            const isReady = stage >= 4;

            // Update plot color
            let fillColor = 0xcd853f;
            if (crop) {
                fillColor = isReady ? 0x8b4513 : 0xa0522d;
            }
            (plotMesh.material as THREE.MeshLambertMaterial).color.setHex(fillColor);

            // Update crop label
            const cropLabel = cropLabelsRef.current.get(plot.id);
            if (cropLabel && crop) {
                const div = cropLabel.userData.div as HTMLDivElement;
                if (stage >= 4) {
                    div.textContent = crop.emoji;
                    div.style.fontSize = `${20 + stage * 2}px`;
                } else if (stage > 0) {
                    div.textContent = '🌱';
                    div.style.fontSize = '16px';
                } else {
                    div.textContent = '';
                }

                // Update glow
                const glowMesh = glowMeshesRef.current.get(plot.id);
                if (glowMesh && isReady) {
                    const bounce = Math.sin(animationFrameRef.current * 0.06 + plot.id) * 0.2;
                    (glowMesh.material as THREE.MeshBasicMaterial).opacity = 0.1 + Math.sin(animationFrameRef.current * 0.08) * 0.05;
                    cropLabel.position.y = 1 + bounce;
                    glowMesh.position.y = 1 + bounce;
                } else if (glowMesh) {
                    (glowMesh.material as THREE.MeshBasicMaterial).opacity = 0;
                }
            } else if (cropLabel) {
                const div = cropLabel.userData.div as HTMLDivElement;
                div.textContent = '';
            }
        });
    }, [gameState.plots, hoveredPlot, animationFrame]);

    // Update visuals when state changes
    useEffect(() => {
        updatePlotVisuals();
    }, [updatePlotVisuals]);

    const redrawPlots = () => {
        const scene = sceneRef.current;
        if (!scene) return;

        const rows = Math.ceil(gameState.plots.length / COLS);
        const totalWidth = (COLS - 1) * TILE_SIZE * 1.5;
        const totalHeight = (rows - 1) * TILE_SIZE * 1.5;
        gridOffsetRef.current = {
            x: -totalWidth / 2,
            y: -totalHeight / 2 + 3
        };

        gameState.plots.forEach((plot, index) => {
            const col = index % COLS;
            const row = Math.floor(index / COLS);
            const worldPos = isoToWorld(col, row, gridOffsetRef.current.x, gridOffsetRef.current.y);

            const plotMesh = plotMeshesRef.current.get(plot.id);
            if (plotMesh) {
                plotMesh.position.copy(worldPos);
            }

            const cropLabel = cropLabelsRef.current.get(plot.id);
            if (cropLabel) {
                cropLabel.position.set(worldPos.x, 1, worldPos.z);
            }

            const glowMesh = glowMeshesRef.current.get(plot.id);
            if (glowMesh) {
                glowMesh.position.set(worldPos.x, 1, worldPos.z);
            }
        });
    };

    const updateCropLabels = () => {
        if (!sceneRef.current) return;

        gameState.plots.forEach((plot) => {
            const cropLabel = cropLabelsRef.current.get(plot.id);
            if (!cropLabel) return;

            const crop = plot.cropId ? CROPS.find(c => c.id === plot.cropId) : null;
            const stage = getGrowthStage(plot);
            const isReady = stage >= 4;

            if (crop && stage > 0) {
                const bounce = isReady ? Math.sin(animationFrameRef.current * 0.06 + plot.id) * 0.2 : 0;
                cropLabel.position.y = 1 + bounce;
            }
        });
    };

    const updateParticles = () => {
        const particlePoints = particlePointsRef.current;
        if (!particlePoints) return;

        particlesRef.current = particlesRef.current.filter(p => {
            p.life--;
            if (p.life <= 0) return false;

            p.x += p.vx;
            p.y += p.vy;
            p.z += p.vz;
            p.vy -= 0.05; // gravity

            return p.life > 0;
        });

        // Update particle geometry
        const positions = particlePoints.geometry.attributes.position.array as Float32Array;
        const colors = particlePoints.geometry.attributes.color.array as Float32Array;

        for (let i = 0; i < particlesRef.current.length; i++) {
            const p = particlesRef.current[i];
            positions[i * 3] = p.x;
            positions[i * 3 + 1] = p.y;
            positions[i * 3 + 2] = p.z;
            colors[i * 3] = ((p.color >> 16) & 255) / 255;
            colors[i * 3 + 1] = ((p.color >> 8) & 255) / 255;
            colors[i * 3 + 2] = (p.color & 255) / 255;
        }

        // Clear remaining positions
        for (let i = particlesRef.current.length; i < 500; i++) {
            positions[i * 3] = 0;
            positions[i * 3 + 1] = -1000;
            positions[i * 3 + 2] = 0;
        }

        particlePoints.geometry.attributes.position.needsUpdate = true;
        particlePoints.geometry.attributes.color.needsUpdate = true;
    };

    const updatePopups = () => {
        popupsRef.current = popupsRef.current.filter(p => {
            p.life--;
            if (p.life <= 0) {
                sceneRef.current?.remove(p.object);
                popupLabelsRef.current.delete(p.id);
                return false;
            }

            p.y += 0.02;
            p.object.position.y = p.y + 2;
            const opacity = p.life / p.maxLife;
            const div = p.object.userData.div as HTMLDivElement;
            div.style.opacity = opacity.toString();
            return p.life > 0;
        });
    };

    const spawnParticles = (worldX: number, worldY: number, worldZ: number, color: number, count: number, type: 'coin' | 'exp' | 'sparkle') => {
        for (let i = 0; i < count; i++) {
            const particle: ParticleData = {
                x: worldX,
                y: worldY,
                z: worldZ,
                vx: (Math.random() - 0.5) * 0.3,
                vy: Math.random() * 0.3 + 0.1,
                vz: (Math.random() - 0.5) * 0.3,
                life: 60 + Math.random() * 30,
                maxLife: 90,
                color,
                size: type === 'coin' ? 0.3 : type === 'exp' ? 0.25 : 0.2,
                type,
            };
            particlesRef.current.push(particle);
        }
    };

    const spawnPopup = (worldX: number, worldY: number, worldZ: number, text: string, color: string) => {
        const scene = sceneRef.current;
        if (!scene) return;

        const div = document.createElement('div');
        div.textContent = text;
        div.style.fontSize = '16px';
        div.style.fontWeight = 'bold';
        div.style.color = color;
        div.style.textShadow = '1px 1px 2px white, -1px -1px 2px white, 1px -1px 2px white, -1px 1px 2px white';
        div.style.pointerEvents = 'none';
        div.style.opacity = '1';

        const label = new CSS2DObject(div);
        label.position.set(worldX, worldY + 2, worldZ);
        scene.add(label);

        const popupId = `popup-${Date.now()}-${Math.random()}`;
        popupsRef.current.push({
            id: popupId,
            x: worldX,
            y: worldY,
            z: worldZ,
            text,
            color,
            life: 80,
            maxLife: 80,
            object: label,
        });
        popupLabelsRef.current.set(popupId, label);
    };

    const getPlotWorldPosition = (plotId: number): THREE.Vector3 | null => {
        const index = gameState.plots.findIndex(p => p.id === plotId);
        if (index === -1) return null;

        const col = index % COLS;
        const row = Math.floor(index / COLS);
        return isoToWorld(col, row, gridOffsetRef.current.x, gridOffsetRef.current.y);
    };

    const handlePlotClick = (plotId: number) => {
        const plot = gameState.plots.find(p => p.id === plotId);
        if (!plot) return;

        // Close seed selection if open
        if (selectedPlot !== null) {
            setSelectedPlot(null);
            if (!plot.cropId) return;
        }

        // Click empty land -> open seed selection
        if (!plot.cropId) {
            setShowCropInfo(false);
            setSelectedPlot(plot.id);
            return;
        }

        // Click planted land -> harvest or show info
        if (getGrowthStage(plot) >= 4) {
            harvestCrop(plotId);
        } else {
            const crop = CROPS.find(c => c.id === plot.cropId);
            if (crop) {
                setSelectedCropForInfo(crop);
                setSelectedPlotForInfo(plot);
                setShowCropInfo(true);
            }
        }
    };

    const getGrowthStage = useCallback((plot: Plot): number => {
        if (!plot.cropId || !plot.plantedAt) return 0;
        const crop = CROPS.find(c => c.id === plot.cropId);
        if (!crop) return 0;
        const elapsed = (currentTime - plot.plantedAt) / 1000;
        const progress = Math.min(elapsed / crop.growTime, 1);
        return Math.floor(progress * 4);
    }, [currentTime]);

    const getRemainingTime = useCallback((plot: Plot): number => {
        if (!plot.cropId || !plot.plantedAt) return 0;
        const crop = CROPS.find(c => c.id === plot.cropId);
        if (!crop) return 0;
        const elapsed = (currentTime - plot.plantedAt) / 1000;
        return Math.max(crop.growTime - elapsed, 0);
    }, [currentTime]);

    const harvestCrop = (plotId: number) => {
        const plot = gameState.plots.find(p => p.id === plotId);
        if (!plot || !plot.cropId) return;
        if (getGrowthStage(plot) < 4) return;

        const cropId = plot.cropId;
        const crop = CROPS.find(c => c.id === cropId);
        if (!crop) return;

        // Get world position for effects
        const pos = getPlotWorldPosition(plotId);
        if (pos) {
            spawnParticles(pos.x, pos.y + 1, pos.z, 0xffd700, 15, 'coin');
            spawnParticles(pos.x, pos.y + 1, pos.z, parseInt(crop.color.replace('#', ''), 16), 10, 'sparkle');
            spawnPopup(pos.x, pos.y, pos.z, `+1 ${crop.name}`, '#90EE90');
            spawnPopup(pos.x, pos.y, pos.z, `+${crop.exp}exp`, '#a855f7');
        }

        setGameState(prev => {
            let newExp = prev.exp + crop.exp;
            let newLevel = prev.level;

            while (newExp >= levelExp(newLevel)) {
                newExp -= levelExp(newLevel);
                newLevel++;
            }

            if (newLevel > prev.level && pos) {
                setTimeout(() => {
                    spawnPopup(pos.x, pos.y, pos.z, '升级!', '#fbbf24');
                }, 200);
            }

            const newHolidaySeeds = { ...prev.holidaySeeds };
            const holidayCrop = tryGetHolidaySeed();
            if (holidayCrop) {
                newHolidaySeeds[holidayCrop.id] = (newHolidaySeeds[holidayCrop.id] || 0) + 1;
                setHolidaySeedCrop(holidayCrop);
                setTimeout(() => {
                    setShowHolidaySeedPopup(true);
                    if (pos) {
                        spawnPopup(pos.x, pos.y, pos.z, `🎁${holidayCrop.name}!`, '#ff6b6b');
                    }
                }, 300);
            }

            return {
                ...prev,
                exp: newExp,
                level: newLevel,
                inventory: {
                    ...prev.inventory,
                    [cropId]: (prev.inventory[cropId] || 0) + 1,
                },
                holidaySeeds: newHolidaySeeds,
                plots: prev.plots.map(p =>
                    p.id === plotId ? { ...p, cropId: null, plantedAt: null } : p
                ),
            };
        });
    };

    const plantCrop = (plotId: number, cropId: string, useHolidaySeed: boolean = false, useSeedInventory: boolean = false) => {
        const crop = CROPS.find(c => c.id === cropId);
        if (!crop) {
            setSelectedPlot(null);
            return;
        }

        const existingPlot = gameState.plots.find(p => p.id === plotId);
        if (existingPlot?.cropId) {
            setSelectedPlot(null);
            return;
        }

        if (useHolidaySeed) {
            const seedCount = gameState.holidaySeeds[cropId] || 0;
            if (seedCount <= 0 || !canPlantHolidayCrop(crop)) {
                setSelectedPlot(null);
                return;
            }

            setGameState(prev => ({
                ...prev,
                holidaySeeds: {
                    ...prev.holidaySeeds,
                    [cropId]: prev.holidaySeeds[cropId] - 1,
                },
                plots: prev.plots.map(p =>
                    p.id === plotId ? { ...p, cropId, plantedAt: Date.now() } : p
                ),
            }));
            setSelectedPlot(null);
            return;
        }

        if (useSeedInventory) {
            const seedCount = gameState.seedInventory[cropId] || 0;
            if (seedCount <= 0) {
                setSelectedPlot(null);
                return;
            }

            setGameState(prev => ({
                ...prev,
                seedInventory: {
                    ...prev.seedInventory,
                    [cropId]: prev.seedInventory[cropId] - 1,
                },
                plots: prev.plots.map(p =>
                    p.id === plotId ? { ...p, cropId, plantedAt: Date.now() } : p
                ),
            }));
            setSelectedPlot(null);
            return;
        }

        if (gameState.coins < crop.seedPrice) {
            setSelectedPlot(null);
            return;
        }

        setGameState(prev => ({
            ...prev,
            coins: prev.coins - crop.seedPrice,
            plots: prev.plots.map(p =>
                p.id === plotId ? { ...p, cropId, plantedAt: Date.now() } : p
            ),
        }));
        setSelectedPlot(null);
    };

    const buySeed = (cropId: string) => {
        const crop = CROPS.find(c => c.id === cropId);
        if (!crop || crop.holiday) return;
        if (gameState.coins < crop.seedPrice) return;

        setGameState(prev => ({
            ...prev,
            coins: prev.coins - crop.seedPrice,
            seedInventory: {
                ...prev.seedInventory,
                [cropId]: (prev.seedInventory[cropId] || 0) + 1,
            },
        }));
    };

    const buySeedBatch = (cropId: string, count: number) => {
        const crop = CROPS.find(c => c.id === cropId);
        if (!crop || crop.holiday) return;
        const totalCost = crop.seedPrice * count;
        if (gameState.coins < totalCost) return;

        setGameState(prev => ({
            ...prev,
            coins: prev.coins - totalCost,
            seedInventory: {
                ...prev.seedInventory,
                [cropId]: (prev.seedInventory[cropId] || 0) + count,
            },
        }));
    };

    const sellCrop = (cropId: string) => {
        if (!gameState.inventory[cropId] || gameState.inventory[cropId] <= 0) return;
        const crop = CROPS.find(c => c.id === cropId);
        if (!crop) return;

        setGameState(prev => ({
            ...prev,
            coins: prev.coins + crop.sellPrice,
            inventory: {
                ...prev.inventory,
                [cropId]: prev.inventory[cropId] - 1,
            },
        }));
    };

    const sellAllCrops = () => {
        const inventory = gameState.inventory;
        const entries = Object.entries(inventory).filter(([, count]) => count > 0);
        if (entries.length === 0) return;

        let totalCoins = 0;
        entries.forEach(([cropId, count]) => {
            const crop = CROPS.find(c => c.id === cropId);
            if (crop) {
                totalCoins += crop.sellPrice * count;
            }
        });

        setGameState(prev => {
            const newInventory = { ...prev.inventory };
            entries.forEach(([cropId]) => {
                newInventory[cropId] = 0;
            });
            return {
                ...prev,
                coins: prev.coins + totalCoins,
                inventory: newInventory,
            };
        });
    };

    const expandFarm = () => {
        const cost = getExpandCost(gameState.plots.length);
        if (gameState.coins < cost) return;
        const newPlotId = Math.max(...gameState.plots.map(p => p.id)) + 1;

        setGameState(prev => ({
            ...prev,
            coins: prev.coins - cost,
            plots: [...prev.plots, { id: newPlotId, cropId: null, plantedAt: null }],
        }));

        // Add new plot to Three.js scene
        setTimeout(() => {
            const scene = sceneRef.current;
            if (!scene) return;

            const index = gameState.plots.length;
            const col = index % COLS;
            const row = Math.floor(index / COLS);

            const rows = Math.ceil((gameState.plots.length + 1) / COLS);
            const totalWidth = (COLS - 1) * TILE_SIZE * 1.5;
            const totalHeight = (rows - 1) * TILE_SIZE * 1.5;
            gridOffsetRef.current = {
                x: -totalWidth / 2,
                y: -totalHeight / 2 + 3
            };

            const worldPos = isoToWorld(col, row, gridOffsetRef.current.x, gridOffsetRef.current.y);

            // Create diamond plot mesh
            const plotGeometry = createDiamondGeometry(TILE_SIZE, TILE_SIZE);
            const plotMaterial = new THREE.MeshLambertMaterial({
                color: 0xcd853f,
            });
            const plotMesh = new THREE.Mesh(plotGeometry, plotMaterial);
            plotMesh.position.copy(worldPos);
            plotMesh.userData = { plotId: newPlotId };
            scene.add(plotMesh);
            plotMeshesRef.current.set(newPlotId, plotMesh);

            // Create crop label
            const cropLabel = createCropLabelObject();
            cropLabel.position.set(worldPos.x, 1, worldPos.z);
            scene.add(cropLabel);
            cropLabelsRef.current.set(newPlotId, cropLabel);

            // Create glow mesh
            const glowGeometry = new THREE.SphereGeometry(1, 16, 16);
            const glowMaterial = new THREE.MeshBasicMaterial({
                color: 0xffd700,
                transparent: true,
                opacity: 0,
            });
            const glowMesh = new THREE.Mesh(glowGeometry, glowMaterial);
            glowMesh.position.set(worldPos.x, 1, worldPos.z);
            scene.add(glowMesh);
            glowMeshesRef.current.set(newPlotId, glowMesh);

            // Redraw all plots to recenter
            redrawPlots();
        }, 50);
    };

    const harvestAll = () => {
        const readyPlots = gameState.plots.filter(p => p.cropId && getGrowthStage(p) >= 4);
        if (readyPlots.length === 0) return;

        let totalCoins = 0;
        let totalExp = 0;

        readyPlots.forEach(plot => {
            if (plot.cropId) {
                const crop = CROPS.find(c => c.id === plot.cropId);
                if (crop) {
                    totalCoins += crop.sellPrice;
                    totalExp += crop.exp;
                    const pos = getPlotWorldPosition(plot.id);
                    if (pos) {
                        spawnParticles(pos.x, pos.y + 1, pos.z, 0xffd700, 8, 'coin');
                    }
                }
            }
        });

        setGameState(prev => {
            let newExp = prev.exp + totalExp;
            let newLevel = prev.level;

            while (newExp >= levelExp(newLevel)) {
                newExp -= levelExp(newLevel);
                newLevel++;
            }

            const newInventory = { ...prev.inventory };

            readyPlots.forEach(plot => {
                if (plot.cropId) {
                    newInventory[plot.cropId] = (newInventory[plot.cropId] || 0) + 1;
                }
            });
            return {
                ...prev,
                coins: prev.coins + totalCoins,
                exp: newExp,
                level: newLevel,
                inventory: newInventory,
                plots: prev.plots.map(p =>
                    readyPlots.some(rp => rp.id === p.id)
                        ? { ...p, cropId: null, plantedAt: null }
                        : p
                ),
            };
        });
    };

    const resetGame = () => {
        if (confirm('确定要重置游戏吗？所有进度将丢失！')) {
            localStorage.removeItem(STORAGE_KEY);
            setGameState({
                coins: 300,
                exp: 0,
                level: 1,
                plots: Array.from({ length: INITIAL_PLOTS }, (_, i) => ({ id: i, cropId: null, plantedAt: null })),
                inventory: {},
                seedInventory: {},
                holidaySeeds: {},
            });

            // Clear and recreate Three.js plots
            const scene = sceneRef.current;
            if (scene) {
                plotMeshesRef.current.forEach((mesh) => {
                    scene.remove(mesh);
                    mesh.geometry.dispose();
                    (mesh.material as THREE.Material).dispose();
                });
                plotMeshesRef.current.clear();

                cropLabelsRef.current.forEach((label) => {
                    scene.remove(label);
                });
                cropLabelsRef.current.clear();

                glowMeshesRef.current.forEach((mesh) => {
                    scene.remove(mesh);
                    mesh.geometry.dispose();
                    (mesh.material as THREE.Material).dispose();
                });
                glowMeshesRef.current.clear();

                popupsRef.current.forEach((p) => {
                    scene.remove(p.object);
                });
                popupsRef.current = [];
                popupLabelsRef.current.clear();

                createPlots();
            }
        }
    };

    const formatTime = (seconds: number): string => {
        if (seconds <= 0) return '完成!';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return mins > 0 ? `${mins}分${secs}秒` : `${secs}秒`;
    };

    const readyToHarvest = gameState.plots.filter(p => p.cropId && getGrowthStage(p) >= 4).length;
    const expProgress = (gameState.exp / levelExp(gameState.level)) * 100;
    const totalInventory = Object.values(gameState.inventory).reduce((a, b) => a + b, 0);

    const getHoveredPlotTooltip = () => {
        if (hoveredPlot === null) return '';
        const plot = gameState.plots.find(p => p.id === hoveredPlot);
        if (!plot) return '';
        if (plot.cropId) {
            const crop = CROPS.find(c => c.id === plot.cropId);
            const stage = getGrowthStage(plot);
            if (stage >= 4) {
                return `✨ 点击收获 ${crop?.emoji} ${crop?.name}！`;
            }
            return `${crop?.emoji} ${crop?.name} - ${formatTime(getRemainingTime(plot))}`;
        }
        return '🌱 点击选择种子';
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-sky-300 to-green-200">
            <SEO title="农场 - LIGHTING博客" description="来我的虚拟农场种菜吧！" />

            <header className="bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-xl sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 py-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Link to="/" className="p-2 hover:bg-green-500 rounded-xl transition">
                                <ChevronLeft className="w-5 h-5" />
                            </Link>
                            <h1 className="text-xl font-bold flex items-center gap-2">
                                🐔 开心农场
                            </h1>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2 bg-green-800/50 px-4 py-2 rounded-full">
                                <Coins className="w-5 h-5 text-yellow-300" />
                                <span className="font-bold text-yellow-300">{gameState.coins}</span>
                            </div>
                            <div className="flex items-center gap-1 bg-purple-800/50 px-3 py-2 rounded-full">
                                <Zap className="w-4 h-4 text-purple-300" />
                                <span className="text-sm font-medium text-purple-200">Lv.{gameState.level}</span>
                            </div>
                        </div>
                    </div>

                    <div className="mt-2 bg-green-800/30 rounded-full h-2 overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-purple-400 to-pink-400 transition-all duration-300"
                            style={{ width: `${expProgress}%` }}
                        />
                    </div>
                    <p className="text-xs text-green-200 mt-1 text-center">
                        {gameState.exp} / {levelExp(gameState.level)} exp
                    </p>
                </div>
            </header>

            <div className="max-w-4xl mx-auto px-4 py-4" ref={containerRef}>
                {/* 仓库栏 - 可折叠 */}
                <div className="bg-white/95 backdrop-blur shadow-xl rounded-2xl mb-4 border border-green-100 overflow-hidden">

                    <button
                        onClick={() => setShowWarehouse(!showWarehouse)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-green-50 transition"
                    >
                        <div className="flex items-center gap-2">
                            <ShoppingCart className="w-5 h-5 text-green-600" />
                            <span className="font-bold text-gray-700">仓库</span>
                            <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-sm font-medium">
                                {totalInventory} 件
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-green-600 font-medium">
                                💰 {Object.entries(gameState.inventory).reduce((sum, [id, count]) => {
                                    const crop = CROPS.find(c => c.id === id);
                                    return sum + (crop ? crop.sellPrice * count : 0);
                                }, 0)}
                            </span>
                            <span className={`text-green-600 transition-transform ${showWarehouse ? 'rotate-180' : ''}`}>
                                ▼
                            </span>
                        </div>
                    </button>

                    <div className={`transition-all duration-300 ${showWarehouse ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                        <div className="px-4 pb-4">
                            {totalInventory > 0 && (
                                <button
                                    onClick={sellAllCrops}
                                    className="w-full mb-3 py-2 px-4 bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-white font-bold rounded-xl shadow-lg transition transform hover:scale-105"
                                >
                                    🎉 批量卖出所有农作物
                                </button>
                            )}
                            <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto custom-scrollbar">
                                {Object.entries(gameState.inventory)
                                    .filter(([, count]) => count > 0)
                                    .map(([id, count]) => {
                                        const crop = CROPS.find(c => c.id === id);
                                        if (!crop) return null;
                                        return (
                                            <button
                                                key={crop.id}
                                                onClick={() => sellCrop(crop.id)}
                                                className="flex items-center gap-2 px-3 py-2 rounded-xl transition transform hover:scale-105 bg-gradient-to-r from-green-100 to-green-50 hover:from-green-200 hover:to-green-100 shadow-md"
                                            >
                                                <span className="text-2xl">{crop.emoji}</span>
                                                <div className="text-left">
                                                    <span className="text-sm font-medium text-gray-700 block">{crop.name}</span>
                                                    <span className="text-xs font-bold text-green-600">x{count}</span>
                                                </div>
                                            </button>
                                        );
                                    })}
                                {totalInventory === 0 && (
                                    <p className="text-gray-400 text-sm py-2 w-full text-center">空空如也，快去种植吧！🌱</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 种子仓库栏 - 可折叠 */}
                <div className="bg-white/95 backdrop-blur shadow-xl rounded-2xl mb-4 border border-green-100 overflow-hidden">
                    <button
                        onClick={() => setShowSeedWarehouse(!showSeedWarehouse)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-green-50 transition"
                    >
                        <div className="flex items-center gap-2">
                            <span className="text-xl">🌱</span>
                            <span className="font-bold text-gray-700">种子仓库</span>
                            <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-sm font-medium">
                                {Object.values(gameState.seedInventory).reduce((a: number, b: number) => a + b, 0) + Object.values(gameState.holidaySeeds).reduce((a: number, b: number) => a + b, 0)} 颗
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className={`text-green-600 transition-transform ${showSeedWarehouse ? 'rotate-180' : ''}`}>
                                ▼
                            </span>
                        </div>
                    </button>

                    <div className={`transition-all duration-300 ${showSeedWarehouse ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                        <div className="px-4 pb-4">
                            <div className="text-xs text-gray-500 mb-2">点击作物直接进入农场种植</div>
                            <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto custom-scrollbar">
                                {/* 普通种子 */}
                                {Object.entries(gameState.seedInventory).filter(([, c]) => c > 0).map(([seedId, count]) => {
                                    const crop = CROPS.find(c => c.id === seedId);
                                    if (!crop) return null;
                                    return (
                                        <button
                                            key={seedId}
                                            onClick={() => { setShowSeedWarehouse(false); }}
                                            className="flex items-center gap-2 px-3 py-2 rounded-xl transition bg-gradient-to-r from-green-100 to-green-50 hover:from-green-200 hover:to-green-100 shadow-md"
                                        >
                                            <span className="text-2xl">{crop.emoji}</span>
                                            <div>
                                                <div className="text-sm font-medium text-gray-700">{crop.name}</div>
                                                <div className="text-xs text-green-600 font-bold">x{count}</div>
                                            </div>
                                        </button>
                                    );
                                })}
                                {/* 节日种子 */}
                                {Object.entries(gameState.holidaySeeds).filter(([, c]) => c > 0).map(([seedId, count]) => {
                                    const crop = CROPS.find(c => c.id === seedId);
                                    if (!crop) return null;
                                    return (
                                        <button
                                            key={seedId}
                                            onClick={() => { setShowSeedWarehouse(false); }}
                                            className="flex items-center gap-2 px-3 py-2 rounded-xl transition bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 hover:from-red-100 hover:to-orange-50"
                                        >
                                            <span className="text-2xl">{crop.emoji}</span>
                                            <div>
                                                <div className="text-sm font-medium text-gray-700">{crop.name}</div>
                                                <div className="text-xs text-red-500 font-bold">🎁x{count}</div>
                                            </div>
                                        </button>
                                    );
                                })}
                                {Object.keys(gameState.seedInventory).filter(k => (gameState.seedInventory[k] || 0) > 0).length === 0 &&
                                 Object.keys(gameState.holidaySeeds).filter(k => (gameState.holidaySeeds[k] || 0) > 0).length === 0 && (
                                    <p className="text-gray-400 text-sm py-2 w-full text-center">种子仓库为空，请购买种子！</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 农场Three.js画布 */}
                <div className="bg-white/95 backdrop-blur shadow-xl rounded-2xl p-4 mb-4 border border-green-100">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-bold text-gray-700 flex items-center gap-2">
                            🌾 我的农场
                            <span className="text-sm text-gray-500 font-normal">({gameState.plots.length}块田)</span>
                        </h2>
                        {readyToHarvest > 0 && (
                            <span className="text-sm text-orange-500 font-bold animate-bounce">
                                ⚡ {readyToHarvest} 个可收获!
                            </span>
                        )}
                    </div>

                    <div className="flex justify-center overflow-auto">
                        <div
                            ref={threeContainerRef}
                            className="max-w-full rounded-xl shadow-inner cursor-pointer"
                            style={{ width: '100%', height: '450px' }}
                        />
                    </div>

                    {hoveredPlot !== null && (
                        <div className="mt-3 text-center text-sm text-gray-600">
                            {getHoveredPlotTooltip()}
                        </div>
                    )}

                    <div className="flex gap-3 mt-4">
                        <button
                            onClick={expandFarm}
                            disabled={gameState.coins < getExpandCost(gameState.plots.length)}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold transition transform hover:scale-105 ${
                                gameState.coins >= getExpandCost(gameState.plots.length)
                                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg'
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                        >
                            <Plus className="w-5 h-5" />
                            开垦 ({getExpandCost(gameState.plots.length)}💰)
                        </button>
                        <button
                            onClick={harvestAll}
                            disabled={readyToHarvest === 0}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold transition transform hover:scale-105 ${
                                readyToHarvest > 0
                                    ? 'bg-gradient-to-r from-orange-400 to-amber-500 hover:from-orange-500 hover:to-amber-600 text-white shadow-lg animate-pulse'
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                        >
                            <Sparkles className="w-5 h-5" />
                            一键收获 ({readyToHarvest})
                        </button>
                        <button
                            onClick={() => setShowShop(true)}
                            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white transition shadow-lg transform hover:scale-105"
                        >
                            <ShoppingCart className="w-5 h-5" />
                            商店
                        </button>
                    </div>
                </div>

                {/* 游戏说明 */}
                <div className="bg-white/90 backdrop-blur rounded-xl p-4 border border-blue-200">
                    <h4 className="font-bold text-blue-700 mb-2 flex items-center gap-2">
                        <Star className="w-4 h-4" />
                        升级攻略
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="bg-green-50 p-3 rounded-xl">
                            <p className="font-bold text-green-700 mb-1">🌱 种植</p>
                            <p className="text-green-600">点击空地 → 选择种子 → 开始生长</p>
                        </div>
                        <div className="bg-orange-50 p-3 rounded-xl">
                            <p className="font-bold text-orange-700 mb-1">🌾 收获</p>
                            <p className="text-orange-600">作物成熟后点击收获，获得金币和经验</p>
                        </div>
                        <div className="bg-purple-50 p-3 rounded-xl">
                            <p className="font-bold text-purple-700 mb-1">📈 升级</p>
                            <p className="text-purple-600">积累经验提升等级，解锁更多玩法</p>
                        </div>
                        <div className="bg-yellow-50 p-3 rounded-xl">
                            <p className="font-bold text-yellow-700 mb-1">💰 扩大</p>
                            <p className="text-yellow-600">金币开垦新田地，农田数量无限制!</p>
                        </div>
                    </div>
                </div>

                {/* 统计信息 */}
                <div className="mt-4 flex justify-center gap-6 text-sm text-gray-600">
                    <span>🏆 等级: {gameState.level}</span>
                    <span>💰 金币: {gameState.coins}</span>
                    <span>🌾 田地: {gameState.plots.length}</span>
                    <button onClick={resetGame} className="text-red-400 hover:text-red-600">
                        <RotateCcw className="w-4 h-4 inline" /> 重置
                    </button>
                </div>
            </div>

            {/* 种子选择弹窗 */}
            {selectedPlot !== null && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedPlot(null)}>
                    <div className="bg-gradient-to-b from-green-50 to-white rounded-3xl w-full max-w-md p-6 shadow-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                            🌱 选择种子种植
                        </h3>

                        {/* 种子仓库 - 已有种子 */}
                        <div className="mb-4">
                            <div className="text-sm font-bold text-green-600 mb-2">📦 种子仓库 (点击直接种植)</div>
                            <div className="grid grid-cols-3 gap-2 max-h-60 overflow-y-auto custom-scrollbar">
                                {/* 普通种子 */}
                                {Object.entries(gameState.seedInventory).filter(([, c]) => c > 0).map(([seedId, count]) => {
                                    const crop = CROPS.find(c => c.id === seedId);
                                    if (!crop) return null;
                                    return (
                                        <button
                                            key={crop.id}
                                            onClick={() => plantCrop(selectedPlot, crop.id, false, true)}
                                            className="p-3 rounded-xl text-center transition transform hover:scale-105 bg-gradient-to-r from-green-100 to-green-50 border-2 border-green-300 hover:border-green-400 shadow"
                                        >
                                            <div className="text-3xl mb-1">{crop.emoji}</div>
                                            <div className="text-xs font-bold text-gray-700">{crop.name}</div>
                                            <div className="text-xs text-green-600 font-bold">x{count}</div>
                                        </button>
                                    );
                                })}
                                {/* 节日种子 */}
                                {Object.entries(gameState.holidaySeeds).filter(([, c]) => c > 0).map(([seedId, count]) => {
                                    const crop = CROPS.find(c => c.id === seedId);
                                    if (!crop) return null;
                                    return (
                                        <button
                                            key={crop.id}
                                            onClick={() => plantCrop(selectedPlot, crop.id, true)}
                                            className="p-3 rounded-xl text-center transition transform hover:scale-105 bg-gradient-to-r from-red-100 to-orange-50 border-2 border-red-300 hover:border-red-400 shadow"
                                        >
                                            <div className="text-3xl mb-1">{crop.emoji}</div>
                                            <div className="text-xs font-bold text-gray-700">{crop.name}</div>
                                            <div className="text-xs text-red-500 font-bold">🎁x{count}</div>
                                        </button>
                                    );
                                })}
                                {Object.keys(gameState.seedInventory).filter(k => (gameState.seedInventory[k] || 0) > 0).length === 0 &&
                                 Object.keys(gameState.holidaySeeds).filter(k => (gameState.holidaySeeds[k] || 0) > 0).length === 0 && (
                                    <p className="col-span-3 text-center text-gray-400 text-sm py-4">种子仓库为空，请去商店购买种子</p>
                                )}
                            </div>
                        </div>

                        <button
                            onClick={() => setSelectedPlot(null)}
                            className="w-full mt-3 py-2 text-gray-500 hover:text-gray-700 font-medium bg-gray-100 rounded-xl"
                        >
                            关闭
                        </button>
                    </div>
                </div>
            )}

            {/* 商店弹窗 */}
            {showShop && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col shadow-2xl">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
                            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                <ShoppingCart className="w-5 h-5 text-blue-500" />
                                种子商店
                            </h3>
                            <button
                                onClick={() => setShowShop(false)}
                                className="p-2 hover:bg-gray-100 rounded-xl text-gray-500"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
                            <div className="space-y-3">
                                {CROPS.filter(crop => !crop.holiday).map(crop => {
                                    const canAfford = gameState.coins >= crop.seedPrice;
                                    const ownedCount = gameState.seedInventory[crop.id] || 0;
                                    return (
                                        <div key={crop.id} className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-2xl">
                                            <div className="flex items-center gap-3">
                                                <span className="text-5xl">{crop.emoji}</span>
                                                <div className="flex-1">
                                                    <h4 className="font-bold text-gray-800">{crop.name}</h4>
                                                    <p className="text-sm text-gray-500">
                                                        ⏱️ {formatTime(crop.growTime)} | 📈 +{crop.exp}经验
                                                    </p>
                                                    {ownedCount > 0 && <p className="text-xs text-green-600">仓库已有: x{ownedCount}</p>}
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-between mt-3">
                                                <div className="text-sm">
                                                    <span className="text-gray-400">成本:</span>
                                                    <span className="font-bold text-yellow-600 ml-1">{crop.seedPrice} 💰</span>
                                                    <span className="text-gray-300 mx-2">|</span>
                                                    <span className="text-gray-400">售价:</span>
                                                    <span className="font-bold text-green-600 ml-1">{crop.sellPrice} 💰</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => buySeed(crop.id)}
                                                        disabled={!canAfford}
                                                        className={`px-3 py-1 rounded-lg text-sm font-bold ${
                                                            canAfford
                                                                ? 'bg-green-500 text-white hover:bg-green-600'
                                                                : 'bg-gray-200 text-gray-400'
                                                        }`}
                                                    >
                                                        购买
                                                    </button>
                                                    <button
                                                        onClick={() => buySeedBatch(crop.id, 5)}
                                                        disabled={!canAfford || gameState.coins < crop.seedPrice * 5}
                                                        className={`px-2 py-1 rounded-lg text-xs font-bold ${
                                                            canAfford && gameState.coins >= crop.seedPrice * 5
                                                                ? 'bg-blue-500 text-white hover:bg-blue-600'
                                                                : 'bg-gray-200 text-gray-400'
                                                        }`}
                                                    >
                                                        x5
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="mt-4 p-3 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border border-red-200">
                                <p className="text-sm text-red-600 font-bold text-center">🎁 节日限定作物无法购买</p>
                                <p className="text-xs text-gray-500 text-center mt-1">收获时有几率获得节日种子！</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 作物信息弹窗 */}
            {showCropInfo && selectedCropForInfo && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowCropInfo(false)}>
                    <div className="bg-white rounded-xl w-full max-w-xs p-4 shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-3 mb-3">
                            <span className="text-5xl">{selectedCropForInfo.emoji}</span>
                            <div>
                                <h3 className="text-lg font-bold text-gray-800">{selectedCropForInfo.name}</h3>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-bold text-white ${
                                    selectedCropForInfo.rarity === 'common' ? 'bg-gray-400' :
                                    selectedCropForInfo.rarity === 'uncommon' ? 'bg-green-500' :
                                    selectedCropForInfo.rarity === 'rare' ? 'bg-blue-500' :
                                    selectedCropForInfo.rarity === 'epic' ? 'bg-purple-500' :
                                    'bg-gradient-to-r from-yellow-500 to-orange-500'
                                }`}>
                                    {selectedCropForInfo.rarity === 'common' ? '普通' :
                                     selectedCropForInfo.rarity === 'uncommon' ? '优秀' :
                                     selectedCropForInfo.rarity === 'rare' ? '稀有' :
                                     selectedCropForInfo.rarity === 'epic' ? '史诗' :
                                     '传说'}
                                </span>
                            </div>
                        </div>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
                                <span className="text-gray-500">⏱️ {selectedPlotForInfo && getGrowthStage(selectedPlotForInfo) < 4 ? '剩余时间' : '总时间'}</span>
                                <span className="font-bold text-gray-800">
                                    {selectedPlotForInfo && getGrowthStage(selectedPlotForInfo) < 4
                                        ? formatTime(getRemainingTime(selectedPlotForInfo))
                                        : formatTime(selectedCropForInfo.growTime)}
                                </span>
                            </div>
                            <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
                                <span className="text-gray-500">🌾 种子/出售</span>
                                <span className="font-bold"><span className="text-yellow-600">{selectedCropForInfo.seedPrice}</span> / <span className="text-green-600">{selectedCropForInfo.sellPrice}</span></span>
                            </div>
                            <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
                                <span className="text-gray-500">📈 经验</span>
                                <span className="font-bold text-purple-600">+{selectedCropForInfo.exp} exp</span>
                            </div>
                            <div className="flex justify-between items-center py-1.5">
                                <span className="text-gray-500">📊 利润</span>
                                <span className="font-bold text-blue-600">+{selectedCropForInfo.sellPrice - selectedCropForInfo.seedPrice} 💰</span>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowCropInfo(false)}
                            className="w-full mt-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded-lg transition text-sm"
                        >
                            关闭
                        </button>
                    </div>
                </div>
            )}

            {/* 节日种子获得弹窗 */}
            {showHolidaySeedPopup && holidaySeedCrop && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowHolidaySeedPopup(false)}>
                    <div className="bg-gradient-to-br from-orange-50 to-red-50 backdrop-blur-sm rounded-2xl w-full max-w-xs p-5 shadow-2xl border border-orange-200" onClick={e => e.stopPropagation()}>
                        <div className="text-center mb-3">
                            <div className="text-5xl mb-2">{holidaySeedCrop.emoji}</div>
                            <h3 className="text-xl font-bold text-gray-800">🎁 获得节日种子!</h3>
                            <p className="text-sm text-orange-600 font-medium">{holidaySeedCrop.holiday?.name}</p>
                        </div>
                        <div className="bg-white/60 rounded-xl p-3 space-y-2 text-sm mb-4">
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600">名称</span>
                                <span className="font-bold text-gray-800">{holidaySeedCrop.name}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600">稀有度</span>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-bold text-white ${
                                    holidaySeedCrop.rarity === 'common' ? 'bg-gray-400' :
                                    holidaySeedCrop.rarity === 'uncommon' ? 'bg-green-500' :
                                    holidaySeedCrop.rarity === 'rare' ? 'bg-blue-500' :
                                    holidaySeedCrop.rarity === 'epic' ? 'bg-purple-500' :
                                    'bg-gradient-to-r from-yellow-500 to-orange-500'
                                }`}>
                                    {holidaySeedCrop.rarity === 'common' ? '普通' :
                                     holidaySeedCrop.rarity === 'uncommon' ? '优秀' :
                                     holidaySeedCrop.rarity === 'rare' ? '稀有' :
                                     holidaySeedCrop.rarity === 'epic' ? '史诗' :
                                     '传说'}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600">🌾 种子/出售</span>
                                <span className="font-bold"><span className="text-yellow-600">{holidaySeedCrop.seedPrice}</span> / <span className="text-green-600">{holidaySeedCrop.sellPrice}</span></span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600">📈 经验</span>
                                <span className="font-bold text-purple-600">+{holidaySeedCrop.exp} exp</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600">⏱️ 生长时间</span>
                                <span className="font-bold text-gray-800">{formatTime(holidaySeedCrop.growTime)}</span>
                            </div>
                        </div>
                        <p className="text-xs text-center text-gray-500 mb-3">种子已存入种子仓库，记得去种植哦~</p>
                        <button
                            onClick={() => setShowHolidaySeedPopup(false)}
                            className="w-full py-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold rounded-lg transition text-sm shadow-lg"
                        >
                            太棒了!
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Farm;
